import { useCallback, useMemo } from 'react';
import I18n from 'I18n';
import { Button } from 'foundations-components/transitional/components/Button';
import { Flex } from 'foundations-components/components/Flex';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import withInlineErrorHandling from 'personalization-lib/components/shared/hocs/withInlineErrorHandling';
import withPersonalizationAccess from 'personalization-lib/components/shared/hocs/withPersonalizationAccess';
import UpgradePointButton from 'personalization-lib/components/shared/upgrades/UpgradePointButton';
import useCreateButtonClickTracker from 'personalization-lib/hooks/tracking/library/useCreateButtonClickTracker';
import useCanCreateVariant from 'personalization-lib/hooks/variantCreation/validation/useCanCreateVariant';
import { usePersonalizationContext } from 'personalization-lib/contexts/PersonalizationContext';
import { getInitialPanelData } from 'personalization-lib/utils/variantCreation/initialData';
import { VALIDATION_ERRORS } from 'personalization-lib/constants/validation';
import BetaBadge from '../shared/badges/BetaBadge';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Button used to open CreateVariantPanel.
 * Checks relevant permissions and shows a tooltip if the button is disabled.
 */
const CreateVariantButton = ({
  label,
  variantInitialData,
  panelProps,
  trackingProps = {},
  buttonProps = {},
  tooltipProps = {},
  upgradeKey,
  showBetaBadge
}) => {
  const initialData = useMemo(() => getInitialPanelData(variantInitialData), [variantInitialData]);
  const {
    openPanel,
    isPanelOpen
  } = usePersonalizationContext();
  const trackButtonClick = useCreateButtonClickTracker({
    actionSource: trackingProps === null || trackingProps === void 0 ? void 0 : trackingProps.actionSource,
    data: initialData
  });
  const handleClick = useCallback(() => {
    openPanel(Object.assign({
      initialData: variantInitialData
    }, panelProps));
    trackButtonClick.createPersonalization();
  }, [openPanel, variantInitialData, trackButtonClick, panelProps]);
  const defaultLabel = I18n.text('personalizationLib.createVariantButton.label.unified');
  const {
    canCreate,
    tooltip,
    validationErrors,
    loading,
    error
  } = useCanCreateVariant(initialData);
  const isUpgradeRequired = validationErrors.has(VALIDATION_ERRORS.MISSING_ASSET_WRITE_SCOPE) && upgradeKey;
  if (isUpgradeRequired) {
    return /*#__PURE__*/_jsx(UpgradePointButton, Object.assign({
      upgradeKey: upgradeKey,
      "data-test-id": "personalize-button-upgrade"
    }, buttonProps, {
      children: label || defaultLabel
    }));
  }
  return /*#__PURE__*/_jsx(Tooltip, Object.assign({}, tooltipProps, {
    title: tooltip,
    disabled: canCreate,
    children: /*#__PURE__*/_jsx(Button, Object.assign({
      onClick: handleClick,
      "data-test-id": !loading && !error && 'create-variant-button',
      "data-onboarding-target": 'create-variant-button'
    }, buttonProps, {
      disabled: !canCreate || isPanelOpen || buttonProps.disabled,
      children: /*#__PURE__*/_jsxs(Flex, {
        gap: "200",
        alignItems: "center",
        children: [label || defaultLabel, showBetaBadge && /*#__PURE__*/_jsx(BetaBadge, {})]
      })
    }))
  }));
};
export default withInlineErrorHandling(withPersonalizationAccess(CreateVariantButton));